import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Loader2, Bot, Save, Plus, Trash2, Power, Users, ScrollText, LayoutDashboard,
  Mail, Clock, ArrowRight, AlertTriangle, Percent, MousePointerClick, MessageCircle, Eye, Send,
} from "lucide-react";

const FUNNELS = [
  { value: "pre_outlook", label: "Funnel Pre-Outlook" },
  { value: "lead", label: "Parcheggio Lead" },
  { value: "progetto", label: "Funnel Preventivo" },
];
const FINAL_ACTIONS = [
  { value: "cemetery", label: "Sposta nel Cimitero" },
  { value: "parking", label: "Sposta nel Parcheggio" },
  { value: "back_to_sales", label: "Riporta al commerciale" },
  { value: "keep_parking", label: "Mantieni in parcheggio" },
];
const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "both", label: "Email + WhatsApp" },
];
const PLACEHOLDERS = [
  { token: "{nome}", label: "Nome" },
  { token: "{prezzo}", label: "Prezzo" },
  { token: "{link_proposta}", label: "Link proposta" },
  { token: "{comune}", label: "Comune" },
  { token: "{citta}", label: "Città" },
  { token: "{telefono}", label: "Telefono" },
  { token: "{email}", label: "Email" },
  { token: "{potenza_stimata}", label: "Potenza stimata" },
  { token: "{accumulo_stimato}", label: "Accumulo stimato" },
  { token: "{data_pratica}", label: "Data creazione pratica" },
  { token: "{costo_corrente}", label: "Costo corrente" },
  { token: "{risparmio_annuo}", label: "Risparmio annuo" },
  { token: "{pbt}", label: "PBT (rientro)" },
  { token: "{copertura}", label: "Copertura fabbisogni %" },
  { token: "{consulente}", label: "Consulente (nome)" },
  { token: "{consulente_tel}", label: "Consulente (cellulare)" },
];
const STEP_TYPES = [
  { value: "message", label: "Messaggio" },
  { value: "question", label: "Domanda (con risposte)" },
];
const ANSWER_ACTIONS = [
  { value: "continue", label: "Continua il funnel" },
  { value: "book_meeting", label: "Fissa un incontro" },
  { value: "notify_referral", label: "Avvisa il referente" },
  { value: "pause", label: "Sospendi / passa al commerciale" },
  { value: "cemetery", label: "Metti in Cimitero" },
];
const funnelLabel = (v) => FUNNELS.find((f) => f.value === v)?.label || v;

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DASHBOARD
// ---------------------------------------------------------------------------
function DashboardTab() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/automations/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  const wfs = d.workflows || [];
  return (
    <div className="space-y-6" data-testid="automations-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Bot} label="Preventivo attivi" value={d.funnel_progetto} testid="stat-progetto" />
        <StatCard icon={Bot} label="Pre-Outlook attivi" value={d.funnel_pre_outlook} testid="stat-pre-outlook" />
        <StatCard icon={Users} label="In parcheggio" value={d.parking} testid="stat-parking" />
        <StatCard icon={Users} label="Nel cimitero" value={d.cemetery} testid="stat-cemetery" />
        <StatCard icon={Mail} label="Email inviate (totali)" value={d.sent_total ?? 0} testid="stat-sent" />
        <StatCard icon={Percent} label="Tasso apertura" value={`${d.open_rate ?? 0}%`} testid="stat-open-rate" />
        <StatCard icon={MousePointerClick} label="Tasso click" value={`${d.click_rate ?? 0}%`} testid="stat-click-rate" />
        <StatCard icon={Power} label="Riattivati" value={d.reactivated} testid="stat-reactivated" />
      </div>

      <Card className="overflow-hidden" data-testid="workflow-stats">
        <div className="px-4 py-3 border-b bg-muted/40 font-semibold text-sm flex items-center gap-2">
          <LayoutDashboard className="h-4 w-4" /> Performance per workflow
        </div>
        {wfs.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">Nessun workflow configurato.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-4 py-2 font-medium">Workflow</th>
                  <th className="px-3 py-2 font-medium text-center">Stato</th>
                  <th className="px-3 py-2 font-medium text-right">Iscritti</th>
                  <th className="px-3 py-2 font-medium text-right">Inviate</th>
                  <th className="px-3 py-2 font-medium text-right">Aperture</th>
                  <th className="px-3 py-2 font-medium text-right">Click</th>
                  <th className="px-3 py-2 font-medium text-right">Conversioni</th>
                </tr>
              </thead>
              <tbody>
                {wfs.map((w) => (
                  <tr key={w.id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`wf-stat-${w.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{w.name}</div>
                      <div className="text-xs text-muted-foreground">{funnelLabel(w.funnel_type)}</div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {w.enabled
                        ? <Badge className="bg-green-600">Attivo</Badge>
                        : <Badge variant="outline">Off</Badge>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{w.enrolled}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{w.sent}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className="font-semibold">{w.open_rate}%</span>
                      <span className="text-xs text-muted-foreground"> ({w.opened})</span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className="font-semibold">{w.click_rate}%</span>
                      <span className="text-xs text-muted-foreground"> ({w.clicked})</span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className="font-semibold text-amber-700">{w.conversion_rate}%</span>
                      <span className="text-xs text-muted-foreground"> ({w.converted})</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CONFIG
// ---------------------------------------------------------------------------
function ConfigTab({ cfg, reloadCfg }) {
  const [local, setLocal] = useState(cfg);
  const [saving, setSaving] = useState(false);
  useEffect(() => setLocal(cfg), [cfg]);
  if (!local) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/automations/config", patch);
      setLocal(data);
      reloadCfg(data);
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleMaster = () => save({ master_enabled: !local.master_enabled });
  const sp = local.score_points || {};

  return (
    <div className="space-y-6" data-testid="automations-config">
      <Card className={`p-5 border-2 ${local.master_enabled ? "border-green-500 bg-green-50/50" : "border-amber-300 bg-amber-50/40"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Power className={`h-6 w-6 mt-0.5 ${local.master_enabled ? "text-green-600" : "text-amber-600"}`} />
            <div>
              <div className="font-semibold text-base">Interruttore generale automazioni</div>
              <div className="text-sm text-muted-foreground">
                {local.master_enabled
                  ? "Le automazioni sono ATTIVE: le email dei funnel abilitati verranno inviate nella fascia oraria consentita."
                  : "Le automazioni sono SPENTE: nessun invio automatico verrà eseguito."}
              </div>
            </div>
          </div>
          <Switch checked={!!local.master_enabled} onCheckedChange={toggleMaster} disabled={saving} data-testid="master-switch" />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="font-semibold">Finestra di invio</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <Label>Ora inizio</Label>
            <Input type="number" min={0} max={23} value={local.send_hours?.start ?? 9}
              onChange={(e) => setLocal({ ...local, send_hours: { ...local.send_hours, start: Number(e.target.value) } })}
              data-testid="send-hour-start" />
          </div>
          <div>
            <Label>Ora fine</Label>
            <Input type="number" min={0} max={24} value={local.send_hours?.end ?? 19}
              onChange={(e) => setLocal({ ...local, send_hours: { ...local.send_hours, end: Number(e.target.value) } })}
              data-testid="send-hour-end" />
          </div>
          <div>
            <Label>Soglia riattivazione</Label>
            <Input type="number" value={local.reactivation_threshold ?? 25}
              onChange={(e) => setLocal({ ...local, reactivation_threshold: Number(e.target.value) })}
              data-testid="reactivation-threshold" />
          </div>
        </div>
        <Button onClick={() => save({ send_hours: local.send_hours, reactivation_threshold: local.reactivation_threshold })}
          disabled={saving} data-testid="save-window-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva finestra
        </Button>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="font-semibold">Punteggi per evento (scoring dinamico)</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.keys(sp).map((k) => (
            <div key={k}>
              <Label className="text-xs">{k}</Label>
              <Input type="number" value={sp[k]}
                onChange={(e) => setLocal({ ...local, score_points: { ...sp, [k]: Number(e.target.value) } })}
                data-testid={`score-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label className="text-xs">Decadimento giornaliero</Label>
            <Input type="number" value={local.score_decay_per_day ?? 1}
              onChange={(e) => setLocal({ ...local, score_decay_per_day: Number(e.target.value) })}
              data-testid="score-decay" />
          </div>
          <Button className="mt-5" onClick={() => save({ score_points: local.score_points, score_decay_per_day: local.score_decay_per_day })}
            disabled={saving} data-testid="save-scoring-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva punteggi
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WORKFLOWS
// ---------------------------------------------------------------------------
const emptyWf = () => ({
  name: "", funnel_type: "pre_outlook", trigger: "", max_communications: 3,
  max_duration_days: 30, final_action: "cemetery", enabled: false,
  steps: [{ channel: "email", wait_hours: 24, subject: "", body: "", whatsapp_template: "", type: "message", options: [] }],
});

function WorkflowDialog({ initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [wf, setWf] = useState(initial || emptyWf());
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testStep, setTestStep] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testSending, setTestSending] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => { if (open) setWf(initial || emptyWf()); }, [open, initial]);

  const setStep = (i, patch) => {
    const steps = wf.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setWf({ ...wf, steps });
  };
  const addStep = () => setWf({ ...wf, steps: [...wf.steps, { channel: "email", wait_hours: 24, subject: "", body: "", whatsapp_template: "", type: "message", options: [] }] });
  const setOption = (i, oi, patch) => setStep(i, { options: (wf.steps[i].options || []).map((o, idx) => (idx === oi ? { ...o, ...patch } : o)) });
  const addOption = (i) => setStep(i, { options: [...(wf.steps[i].options || []), { label: "", action: "continue", confirm_text: "" }] });
  const rmOption = (i, oi) => setStep(i, { options: (wf.steps[i].options || []).filter((_, idx) => idx !== oi) });
  const rmStep = (i) => setWf({ ...wf, steps: wf.steps.filter((_, idx) => idx !== i) });
  const insertPlaceholder = (token) => {
    if (!activeField) { toast.info("Clicca prima nel campo dove inserire il segnaposto"); return; }
    const { i, field } = activeField;
    setStep(i, { [field]: (wf.steps[i]?.[field] || "") + token });
  };
  const doPreview = async (s) => {
    try {
      const { data } = await api.post("/automations/preview", {
        subject: s.subject || "", body: s.body || "", whatsapp_template: s.whatsapp_template || "",
      });
      setPreview({ ...data, channel: s.channel || "email" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!testStep) return;
    const ch = testStep.channel || "email";
    if ((ch === "email" || ch === "both") && !testEmail.trim()) { toast.error("Inserisci l'email di test"); return; }
    if (ch === "whatsapp" && !testPhone.trim()) { toast.error("Inserisci il numero WhatsApp di test"); return; }
    setTestSending(true);
    try {
      const { data } = await api.post("/automations/send-test", {
        subject: testStep.subject || "", body: testStep.body || "", whatsapp_template: testStep.whatsapp_template || "",
        channel: ch, to_email: testEmail.trim(), to_phone: testPhone.trim(),
        type: testStep.type || "message",
        options: (testStep.options || []).map((o) => ({ label: o.label || "", action: o.action || "continue", confirm_text: o.confirm_text || "" })),
      });
      if (data.email?.queued) toast.success(`Email di test inviata a ${data.email.to}`);
      if (data.whatsapp?.pending) toast.info(data.whatsapp.message || "WhatsApp non ancora attivo (Fase 7)");
      setTestStep(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTestSending(false); }
  };

  const save = async () => {
    if (!wf.name.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      const body = {
        name: wf.name, funnel_type: wf.funnel_type, trigger: wf.trigger,
        max_communications: Number(wf.max_communications), max_duration_days: Number(wf.max_duration_days),
        final_action: wf.final_action, enabled: !!wf.enabled,
        steps: wf.steps.map((s) => ({
          channel: s.channel || "email", wait_hours: Number(s.wait_hours) || 0,
          subject: s.subject || "", body: s.body || "", whatsapp_template: s.whatsapp_template || "",
          type: s.type || "message",
          options: (s.options || []).map((o) => ({ label: o.label || "", action: o.action || "continue", confirm_text: o.confirm_text || "" })),
        })),
      };
      if (isEdit) await api.put(`/automations/workflows/${initial.id}`, body);
      else await api.post("/automations/workflows", body);
      toast.success(isEdit ? "Workflow aggiornato" : "Workflow creato");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="outline" size="sm" data-testid={`edit-wf-${initial.id}`}>Modifica</Button>
          : <Button data-testid="new-wf-btn"><Plus className="h-4 w-4 mr-2" /> Nuovo workflow</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wf-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Modifica workflow" : "Nuovo workflow"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome</Label>
              <Input value={wf.name} onChange={(e) => setWf({ ...wf, name: e.target.value })} data-testid="wf-name" />
            </div>
            <div>
              <Label>Funnel</Label>
              <Select value={wf.funnel_type} onValueChange={(v) => setWf({ ...wf, funnel_type: v })}>
                <SelectTrigger data-testid="wf-funnel"><SelectValue /></SelectTrigger>
                <SelectContent>{FUNNELS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Azione finale</Label>
              <Select value={wf.final_action} onValueChange={(v) => setWf({ ...wf, final_action: v })}>
                <SelectTrigger data-testid="wf-final-action"><SelectValue /></SelectTrigger>
                <SelectContent>{FINAL_ACTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max comunicazioni</Label>
              <Input type="number" value={wf.max_communications} onChange={(e) => setWf({ ...wf, max_communications: e.target.value })} data-testid="wf-max-comm" />
            </div>
            <div>
              <Label>Durata max (giorni)</Label>
              <Input type="number" value={wf.max_duration_days} onChange={(e) => setWf({ ...wf, max_duration_days: e.target.value })} data-testid="wf-max-days" />
            </div>
            <div className="col-span-2">
              <Label>Trigger (descrizione)</Label>
              <Input value={wf.trigger} onChange={(e) => setWf({ ...wf, trigger: e.target.value })} data-testid="wf-trigger" />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm">Passi ({wf.steps.length})</div>
              <Button variant="outline" size="sm" onClick={addStep} data-testid="wf-add-step"><Plus className="h-4 w-4 mr-1" /> Passo</Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs rounded-md bg-muted/40 p-2" data-testid="placeholders-bar">
              <span className="text-muted-foreground mr-1">Segnaposto (clicca nel campo, poi qui):</span>
              {PLACEHOLDERS.map((p) => (
                <button type="button" key={p.token} onClick={() => insertPlaceholder(p.token)}
                  className="px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                  title={p.label} data-testid={`ph-${p.token.replace(/[{}]/g, "")}`}>{p.token}</button>
              ))}
            </div>
            {wf.steps.map((s, i) => {
              const ch = s.channel || "email";
              const showEmail = ch === "email" || ch === "both";
              const showWa = ch === "whatsapp" || ch === "both";
              const isQuestion = (s.type || "message") === "question";
              return (
              <Card key={i} className="p-3 space-y-2 bg-muted/30" data-testid={`wf-step-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Passo {i + 1}</div>
                  {wf.steps.length > 1 && <Button variant="ghost" size="sm" onClick={() => rmStep(i)} data-testid={`wf-rm-step-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={s.type || "message"} onValueChange={(v) => setStep(i, { type: v })}>
                      <SelectTrigger data-testid={`wf-step-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STEP_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Canale</Label>
                    <Select value={ch} onValueChange={(v) => setStep(i, { channel: v })}>
                      <SelectTrigger data-testid={`wf-step-channel-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1"><Clock className="h-3 w-3" /> Attesa (ore)</Label>
                    <Input type="number" value={s.wait_hours} onChange={(e) => setStep(i, { wait_hours: e.target.value })} data-testid={`wf-step-wait-${i}`} />
                  </div>
                </div>
                {showEmail && (
                  <>
                    <Input placeholder="Oggetto email" value={s.subject} onFocus={() => setActiveField({ i, field: "subject" })} onChange={(e) => setStep(i, { subject: e.target.value })} data-testid={`wf-step-subject-${i}`} />
                    <Textarea placeholder="Testo dell'email" rows={3} value={s.body} onFocus={() => setActiveField({ i, field: "body" })} onChange={(e) => setStep(i, { body: e.target.value })} data-testid={`wf-step-body-${i}`} />
                  </>
                )}
                {showWa && (
                  <div>
                    <Label className="text-xs flex items-center gap-1"><MessageCircle className="h-3 w-3 text-green-600" /> Messaggio WhatsApp</Label>
                    <Textarea placeholder="Testo del messaggio WhatsApp" rows={3} value={s.whatsapp_template || ""} onFocus={() => setActiveField({ i, field: "whatsapp_template" })} onChange={(e) => setStep(i, { whatsapp_template: e.target.value })} data-testid={`wf-step-whatsapp-${i}`} />
                  </div>
                )}
                {isQuestion && (
                  <div className="rounded-md border border-dashed border-amber-300 p-2 space-y-2" data-testid={`wf-step-options-${i}`}>
                    <div className="text-xs font-semibold flex items-center justify-between">
                      <span>Risposte (pulsanti nell'email)</span>
                      <Button type="button" variant="outline" size="sm" onClick={() => addOption(i)} data-testid={`wf-add-option-${i}`}><Plus className="h-3 w-3 mr-1" /> Risposta</Button>
                    </div>
                    {(s.options || []).map((o, oi) => (
                      <div key={oi} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-white/70 rounded p-2" data-testid={`wf-option-${i}-${oi}`}>
                        <Input className="sm:col-span-3" placeholder="Testo pulsante" value={o.label || ""} onChange={(e) => setOption(i, oi, { label: e.target.value })} data-testid={`wf-option-label-${i}-${oi}`} />
                        <div className="sm:col-span-4">
                          <Select value={o.action || "continue"} onValueChange={(v) => setOption(i, oi, { action: v })}>
                            <SelectTrigger data-testid={`wf-option-action-${i}-${oi}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{ANSWER_ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Input className="sm:col-span-4" placeholder="Messaggio di conferma al cliente" value={o.confirm_text || ""} onChange={(e) => setOption(i, oi, { confirm_text: e.target.value })} data-testid={`wf-option-confirm-${i}-${oi}`} />
                        <Button type="button" variant="ghost" size="sm" className="sm:col-span-1" onClick={() => rmOption(i, oi)} data-testid={`wf-rm-option-${i}-${oi}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    ))}
                    {(s.options || []).length === 0 && <div className="text-[11px] text-muted-foreground">Aggiungi almeno una risposta.</div>}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-muted-foreground">
                    I link inseriti nel testo vengono tracciati automaticamente.{showWa ? " L'invio WhatsApp richiede l'integrazione Meta (Fase 7)." : ""}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button type="button" variant="outline" size="sm" onClick={() => { setTestStep(s); setTestEmail(""); setTestPhone(""); }} data-testid={`wf-step-sendtest-${i}`}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Invia test
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => doPreview(s)} data-testid={`wf-step-preview-${i}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Anteprima
                    </Button>
                  </div>
                </div>
              </Card>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="wf-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={testStep !== null} onOpenChange={(o) => { if (!o) setTestStep(null); }}>
        <DialogContent className="max-w-md" data-testid="test-send-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" /> Invia test</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Il messaggio usa i dati di un lead campione e gli stessi segnaposto dell'invio reale.</p>
            {(testStep?.channel === "email" || testStep?.channel === "both" || !testStep?.channel) && (
              <div className="space-y-1">
                <Label className="text-xs">Email destinatario</Label>
                <Input type="email" placeholder="nome@esempio.it" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email-input" />
              </div>
            )}
            {(testStep?.channel === "whatsapp" || testStep?.channel === "both") && (
              <div className="space-y-1">
                <Label className="text-xs">Numero WhatsApp destinatario</Label>
                <Input placeholder="+39 333 1234567" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="test-phone-input" />
                <p className="text-[11px] text-amber-600">L'invio WhatsApp sarà attivo dopo l'integrazione Meta (Fase 7).</p>
              </div>
            )}
            <div className="flex justify-end">
              <Button size="sm" onClick={sendTest} disabled={testSending} data-testid="test-send-btn">
                {testSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Invia test
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4" /> Anteprima messaggio</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">Dati di esempio del lead: <b>{preview.sample_name}</b></div>
              {(preview.channel === "email" || preview.channel === "both") && (
                <div className="rounded-lg border overflow-hidden" data-testid="preview-email">
                  <div className="bg-muted/50 px-3 py-2 text-xs font-semibold flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</div>
                  <div className="px-3 py-2 border-b text-sm"><span className="text-muted-foreground">Oggetto: </span>{preview.subject || <em className="text-muted-foreground">(vuoto)</em>}</div>
                  <div className="px-3 py-2 text-sm whitespace-pre-wrap">{preview.body || <em className="text-muted-foreground">(vuoto)</em>}</div>
                </div>
              )}
              {(preview.channel === "whatsapp" || preview.channel === "both") && (
                <div className="rounded-lg border overflow-hidden" data-testid="preview-whatsapp">
                  <div className="bg-green-50 text-green-800 px-3 py-2 text-xs font-semibold flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</div>
                  <div className="px-3 py-2 text-sm whitespace-pre-wrap bg-[#e7ffdb]/40">{preview.whatsapp || <em className="text-muted-foreground">(vuoto)</em>}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="preview-close-btn">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function WorkflowsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/automations/workflows");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (w) => {
    try {
      await api.post(`/automations/workflows/${w.id}/toggle`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (w) => {
    if (!window.confirm(`Eliminare "${w.name}"?`)) return;
    try { await api.delete(`/automations/workflows/${w.id}`); load(); toast.success("Eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="automations-workflows">
      <div className="flex justify-end"><WorkflowDialog onSaved={load} /></div>
      {items.length === 0 && <Card className="p-8 text-center text-muted-foreground">Nessun workflow. Creane uno.</Card>}
      {items.map((w) => (
        <Card key={w.id} className="p-4" data-testid={`wf-card-${w.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{w.name}</span>
                <Badge variant="secondary">{funnelLabel(w.funnel_type)}</Badge>
                {w.enabled
                  ? <Badge className="bg-green-600" data-testid={`wf-badge-on-${w.id}`}>Attivo</Badge>
                  : <Badge variant="outline" data-testid={`wf-badge-off-${w.id}`}>Disattivo</Badge>}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {w.steps?.length || 0} passi · max {w.max_communications} comunicazioni · {w.max_duration_days} giorni
              </div>
              {w.trigger && <div className="text-xs text-muted-foreground mt-1">Trigger: {w.trigger}</div>}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!w.enabled} onCheckedChange={() => toggle(w)} data-testid={`wf-toggle-${w.id}`} />
              <WorkflowDialog initial={w} onSaved={load} />
              <Button variant="ghost" size="sm" onClick={() => del(w)} data-testid={`wf-delete-${w.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ENROLLMENTS
// ---------------------------------------------------------------------------
function EnrollmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/automations/enrollments").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-2" data-testid="automations-enrollments">
      {items.length === 0 && <Card className="p-8 text-center text-muted-foreground">Nessun lead iscritto ai funnel.</Card>}
      {items.map((e) => (
        <Card key={e.id} className="p-3 flex items-center justify-between" data-testid={`enroll-${e.id}`}>
          <div className="text-sm">
            <span className="font-medium">{funnelLabel(e.funnel_type)}</span>
            <span className="text-muted-foreground"> · lead {String(e.lead_id).slice(-6)} · passo {(e.step_idx ?? 0) + 1} · inviati {e.sent_count || 0}</span>
          </div>
          <Badge variant={e.status === "active" ? "default" : "outline"}>{e.status}</Badge>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LOGS
// ---------------------------------------------------------------------------
function LogsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/automations/logs?limit=100").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-1" data-testid="automations-logs">
      {items.length === 0 && <Card className="p-8 text-center text-muted-foreground">Nessun evento registrato.</Card>}
      {items.map((l) => (
        <div key={l.id} className="text-xs flex items-center gap-3 py-1.5 border-b border-border/50">
          <span className="text-muted-foreground w-36 shrink-0">{new Date(l.at).toLocaleString("it-IT")}</span>
          <Badge variant="outline" className="shrink-0">{l.event}</Badge>
          <span className="truncate">{l.detail}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN
// ---------------------------------------------------------------------------
export default function AutomazioniCRM() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/automations/config").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="automazioni-crm-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center"><Bot className="h-6 w-6" /></div>
        <div>
          <h1 className="text-2xl font-bold">Automazioni CRM</h1>
          <p className="text-sm text-muted-foreground">Motore di workflow email per il ciclo di vita dei lead con scoring dinamico.</p>
        </div>
      </div>

      {cfg && !cfg.master_enabled && (
        <Card className="p-3 border-amber-300 bg-amber-50 flex items-center gap-2 text-sm text-amber-800" data-testid="master-off-banner">
          <AlertTriangle className="h-4 w-4" /> Le automazioni sono spente. Attiva l'interruttore generale nella scheda "Configurazione" per avviare gli invii.
        </Card>
      )}

      <Tabs defaultValue="dashboard">
        <TabsList data-testid="automations-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Power className="h-4 w-4 mr-1" /> Configurazione</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="tab-workflows"><Bot className="h-4 w-4 mr-1" /> Workflow</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments"><Users className="h-4 w-4 mr-1" /> Iscrizioni</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><ScrollText className="h-4 w-4 mr-1" /> Log</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><DashboardTab /></TabsContent>
        <TabsContent value="config" className="mt-4"><ConfigTab cfg={cfg} reloadCfg={setCfg} /></TabsContent>
        <TabsContent value="workflows" className="mt-4"><WorkflowsTab /></TabsContent>
        <TabsContent value="enrollments" className="mt-4"><EnrollmentsTab /></TabsContent>
        <TabsContent value="logs" className="mt-4"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
